/**
 * Name formatting utilities
 *
 * Handles proper capitalization of names while respecting:
 * - Prefixes: Mr., Mrs., Dr., Rev.
 * - Suffixes: Jr., Sr., III, PhD, MD
 * - Special cases: McDonald, O'Brien, van der, de la
 * - Hyphenated names: Mary-Jane, Jean-Claude
 */

import type { NameFormattingResult, ParsedName } from './types';
import { normalizeWhitespace, capitalizeWords, isAllCaps } from './text';

/**
 * Known prefixes (titles)
 */
const NAME_PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Prof.', 'Rev.', 'Fr.', 'Sr.'];

/**
 * Known suffixes
 */
const NAME_SUFFIXES = [
  'Jr.', 'Jr', 'Sr.', 'Sr', 'II', 'III', 'IV', 'V', 'VI',
  'PhD', 'Ph.D.', 'MD', 'M.D.', 'DDS', 'Esq.', 'CPA',
];

/**
 * Compound last name particles that should stay lowercase
 * Includes both single words and multi-word connectors
 */
const LOWERCASE_PARTICLES = ['van', 'von', 'de', 'del', 'della', 'di', 'da', 'le', 'la', 'der', 'den', 'los'];

/**
 * Special case last name patterns
 * These require custom capitalization rules
 */
const SPECIAL_PATTERNS: Record<string, (name: string) => string> = {
  // Scottish/Irish: McDonald, MacPherson, O'Brien
  mc: (name) => {
    if (name.length <= 2) return name;
    return 'Mc' + name.charAt(2).toUpperCase() + name.slice(3).toLowerCase();
  },
  mac: (name) => {
    if (name.length <= 3) return name;
    return 'Mac' + name.charAt(3).toUpperCase() + name.slice(4).toLowerCase();
  },

  // Irish: O'Brien, O'Connor
  "o'": (name) => {
    const parts = name.split("'");
    if (parts.length === 2) {
      return "O'" + parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
    }
    return name;
  },

  // Dutch: van der, van den, van de
  'van der': (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 3 && parts[0] === 'van' && parts[1] === 'der') {
      const lastName = parts.slice(2).join(' ');
      return 'van der ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },
  'van den': (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 3 && parts[0] === 'van' && parts[1] === 'den') {
      const lastName = parts.slice(2).join(' ');
      return 'van den ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },
  'van de': (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 3 && parts[0] === 'van' && parts[1] === 'de') {
      const lastName = parts.slice(2).join(' ');
      return 'van de ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },

  // German: von
  von: (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 2 && parts[0] === 'von') {
      const lastName = parts.slice(1).join(' ');
      return 'von ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },

  // Spanish/Portuguese: de la, de los, del
  'de la': (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 3 && parts[0] === 'de' && parts[1] === 'la') {
      const lastName = parts.slice(2).join(' ');
      return 'de la ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },
  'de los': (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 3 && parts[0] === 'de' && parts[1] === 'los') {
      const lastName = parts.slice(2).join(' ');
      return 'de los ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },
  del: (name) => {
    const parts = name.toLowerCase().split(' ');
    if (parts.length >= 2 && parts[0] === 'del') {
      const lastName = parts.slice(1).join(' ');
      return 'del ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);
    }
    return name;
  },
};

/**
 * Format a last name with special case handling
 *
 * @example
 * formatLastName('mcdonald') // { formatted: 'McDonald' }
 * formatLastName("o'brien") // { formatted: "O'Brien" }
 * formatLastName('van der berg') // { formatted: 'van der Berg' }
 */
export function formatLastName(value: string): NameFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;
  const lowerName = normalized.toLowerCase();

  // Check for all caps
  const warnings: string[] = [];
  if (isAllCaps(normalized, 3)) {
    warnings.push('Name appears to be in all caps - converted to title case');
  }

  // Check for special patterns (check longer patterns first)
  const patternEntries = Object.entries(SPECIAL_PATTERNS);
  // Sort by pattern length (longest first) to match more specific patterns first
  patternEntries.sort((a, b) => b[0].length - a[0].length);

  for (const [pattern, formatter] of patternEntries) {
    if (lowerName.startsWith(pattern)) {
      const formatted = formatter(normalized);
      return {
        formatted,
        warnings: warnings.length > 0 ? warnings : undefined,
      };
    }
  }

  // Default: capitalize words (handles hyphens and regular names)
  const result = capitalizeWords(normalized);
  return {
    formatted: result.formatted,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Format a first name
 * Progressive formatting - works as user types
 *
 * @example
 * formatFirstName('john') // { formatted: 'John' }
 * formatFirstName('mary-jane') // { formatted: 'Mary-Jane' }
 * formatFirstName('JOHN') // { formatted: 'John', warnings: ['Name appears to be in all caps - converted to title case'] }
 */
export function formatFirstName(value: string): NameFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;

  // Check for all caps
  const warnings: string[] = [];
  if (isAllCaps(normalized, 3)) {
    warnings.push('Name appears to be in all caps - converted to title case');
  }

  // Capitalize words (handles hyphens: Mary-Jane)
  const result = capitalizeWords(normalized);

  return {
    formatted: result.formatted,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Parse a full name into components
 * Extracts prefix, first, middle, last, suffix
 *
 * @example
 * parseFullName('Dr. John Michael Smith Jr.')
 * // {
 * //   prefix: 'Dr.',
 * //   firstName: 'John',
 * //   middleName: 'Michael',
 * //   lastName: 'Smith',
 * //   suffix: 'Jr.'
 * // }
 */
export function parseFullName(value: string): ParsedName | null {
  if (!value) {
    return null;
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;
  const parts = normalized.split(' ');

  if (parts.length === 0) {
    return null;
  }

  let prefix: string | undefined;
  let firstName: string;
  let middleName: string | undefined;
  let lastName: string;
  let suffix: string | undefined;

  let startIndex = 0;
  let endIndex = parts.length;

  // Check for prefix (can be multiple: Prof. Dr.)
  const prefixParts: string[] = [];
  // eslint-disable-next-line no-loop-func
  while (startIndex < parts.length && NAME_PREFIXES.some(p => p.toLowerCase() === parts[startIndex].toLowerCase())) {
    prefixParts.push(parts[startIndex]);
    startIndex++;
  }
  if (prefixParts.length > 0) {
    prefix = prefixParts.join(' ');
  }

  // Check for suffix (can be multiple: Jr. PhD)
  const suffixParts: string[] = [];
  // eslint-disable-next-line no-loop-func
  while (endIndex > startIndex && NAME_SUFFIXES.some(s => s.toLowerCase() === parts[endIndex - 1].toLowerCase())) {
    suffixParts.unshift(parts[endIndex - 1]);
    endIndex--;
  }
  if (suffixParts.length > 0) {
    suffix = suffixParts.join(' ');
  }

  // Extract name parts
  const nameParts = parts.slice(startIndex, endIndex);

  if (nameParts.length === 0) {
    return null;
  }

  if (nameParts.length === 1) {
    // Only one name part - no last name, return null
    return null;
  }

  // Check for compound last names (van der, de la, etc.)
  let lastNameStartIndex = nameParts.length - 1;

  // Look for particles starting from second-to-last word
  for (let i = nameParts.length - 2; i >= 1; i--) {
    const word = nameParts[i].toLowerCase();
    if (LOWERCASE_PARTICLES.includes(word)) {
      lastNameStartIndex = i;
    } else {
      // Stop at first non-particle
      break;
    }
  }

  if (nameParts.length === 2) {
    // First and last (or compound last)
    firstName = nameParts[0];
    lastName = nameParts.slice(lastNameStartIndex).join(' ');
  } else {
    // First, middle(s), last
    firstName = nameParts[0];
    lastName = nameParts.slice(lastNameStartIndex).join(' ');
    if (lastNameStartIndex > 1) {
      middleName = nameParts.slice(1, lastNameStartIndex).join(' ');
    }
  }

  return {
    prefix,
    firstName,
    middleName,
    lastName,
    suffix,
  };
}

/**
 * Capitalize a prefix (title) like Dr., Prof., Mr.
 */
function capitalizePrefix(prefix: string): string {
  const parts = prefix.split(' ');
  return parts
    .map(p => {
      // Handle abbreviated titles with periods
      if (p.includes('.')) {
        const [abbr, ...rest] = p.split('.');
        return abbr.charAt(0).toUpperCase() + abbr.slice(1).toLowerCase() + '.' + rest.join('.');
      }
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Capitalize a suffix like Jr., Sr., PhD
 */
function capitalizeSuffix(suffix: string): string {
  const parts = suffix.split(' ');
  return parts
    .map(p => {
      // Handle abbreviated suffixes with periods
      if (p.includes('.')) {
        const [abbr, ...rest] = p.split('.');
        return abbr.charAt(0).toUpperCase() + abbr.slice(1).toLowerCase() + '.' + rest.join('.');
      }
      // Handle all-caps suffixes like PhD, MD
      if (p.length <= 4 && p.toUpperCase() === p) {
        return p.toUpperCase();
      }
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Format a full name with proper capitalization
 *
 * @example
 * formatFullName('john mcdonald') // { formatted: 'John McDonald' }
 * formatFullName("o'brien") // { formatted: "O'Brien" }
 * formatFullName('mary-jane smith') // { formatted: 'Mary-Jane Smith' }
 * formatFullName('dr. john smith jr.') // { formatted: 'Dr. John Smith Jr.' }
 */
export function formatFullName(value: string): NameFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;

  // Check for all caps (use minLength of 5 to catch short names like "JOHN SMITH")
  const warnings: string[] = [];
  if (isAllCaps(normalized, 5)) {
    warnings.push('Name appears to be in all caps - converted to title case');
  }

  // Parse into components
  const parsed = parseFullName(normalized);

  if (!parsed) {
    // Couldn't parse (single word), just capitalize words
    const result = capitalizeWords(normalized);
    return {
      formatted: result.formatted,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  }

  // Format each component
  const parts: string[] = [];

  if (parsed.prefix) {
    parts.push(capitalizePrefix(parsed.prefix));
  }

  if (parsed.firstName) {
    const formattedFirst = formatFirstName(parsed.firstName);
    parts.push(formattedFirst.formatted);
  }

  if (parsed.middleName) {
    const formattedMiddle = formatFirstName(parsed.middleName);
    parts.push(formattedMiddle.formatted);
  }

  if (parsed.lastName) {
    const formattedLast = formatLastName(parsed.lastName);
    parts.push(formattedLast.formatted);
  }

  if (parsed.suffix) {
    parts.push(capitalizeSuffix(parsed.suffix));
  }

  return {
    formatted: parts.join(' '),
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}
